<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import type {
    CancelReplanMode,
    CancelReplanWorkflowEvent,
    CancelReplanWorkflowOutputs,
  } from "../../../../_logic/cancel-replan-contract"

  let {
    mode = "rescheduleAppointments",
    outputs,
    readOnly = false,
    finishing = false,
    onEvent,
  }: {
    mode?: CancelReplanMode
    outputs: CancelReplanWorkflowOutputs
    readOnly?: boolean
    finishing?: boolean
    onEvent: (event: CancelReplanWorkflowEvent) => void | Promise<void>
  } = $props()

  const numberFormatter = new Intl.NumberFormat("de-DE")
  const canceledAppointmentCount = $derived(
    outputs.cancel?.rows.filter((row) => !row.removed).length ?? 0,
  )
  const plannedAppointmentCount = $derived(
    outputs.planning?.plannedRows.filter((row) => row.event).length ?? 0,
  )
  const createdAnnouncementCount = $derived(outputs.announce?.result.created.length ?? 0)
  const failedAnnouncementCount = $derived(outputs.announce?.result.failed.length ?? 0)
  const canceledAnnouncementCount = $derived(outputs.cancelAnnouncements?.canceled.length ?? 0)
  const completedAnnouncementCount = $derived(outputs.cancelAnnouncements?.completed.length ?? 0)
  const skippedAnnouncementCount = $derived(outputs.cancelAnnouncements?.skipped.length ?? 0)
  const cancelAnnouncementAction = $derived(outputs.cancelAnnouncements?.action ?? "none")

  const title = $derived.by(() => {
    if (mode === "createAppointments") return "Termine angelegt"

    if (mode === "createAnnouncementPlans") return "Ankündigungspläne angelegt"

    if (mode === "cancelAnnouncementPlans") {
      if (cancelAnnouncementAction === "completed") return "Ankündigungspläne abgeschlossen"

      if (cancelAnnouncementAction === "canceled") return "Ankündigungspläne abgebrochen"

      return "Ankündigungspläne beendet"
    }

    return "Termine verschoben"
  })

  const finish = async () => {
    if (finishing) return

    await onEvent({
      type: "summary.finished",
      output: { finished: true },
    })
  }
</script>

<div class="wrapper">
  <p class="hint">{title}. Hier sehen Sie die wichtigsten Ergebnisse.</p>

  <section class="overview-grid">
    {#if mode === "rescheduleAppointments"}
      <article class="result-card">
        <h3>Abgebrochen</h3>
        <p class="metric">{numberFormatter.format(canceledAppointmentCount)}</p>
        <p class="subtle">alte Termine</p>
      </article>
    {/if}

    {#if mode === "rescheduleAppointments" || mode === "createAppointments"}
      <article class="result-card">
        <h3>Geplant</h3>
        <p class="metric">{numberFormatter.format(plannedAppointmentCount)}</p>
        <p class="subtle">neue Termine</p>
      </article>
    {/if}

    {#if mode === "createAnnouncementPlans" || mode === "cancelAnnouncementPlans"}
      <article class="result-card">
        <h3>
          {cancelAnnouncementAction === "completed"
            ? "Abgeschlossen"
            : cancelAnnouncementAction === "canceled"
              ? "Abgebrochen"
              : "Beendet"}
        </h3>
        <p class="metric">
          {numberFormatter.format(
            cancelAnnouncementAction === "completed"
              ? completedAnnouncementCount
              : canceledAnnouncementCount,
          )}
        </p>
        <p class="subtle">Ankündigungspläne</p>
      </article>
    {/if}

    {#if mode !== "cancelAnnouncementPlans"}
      <article class="result-card">
        <h3>{mode === "rescheduleAppointments" ? "Informiert" : "Angelegt"}</h3>
        <p class="metric">{numberFormatter.format(createdAnnouncementCount)}</p>
        <p class="subtle"
          >{mode === "rescheduleAppointments" ? "Kundeninformationen" : "Ankündigungspläne"}</p
        >
      </article>
    {/if}

    <article class="result-card">
      <h3>Hinweise</h3>
      <p class="metric">
        {numberFormatter.format(failedAnnouncementCount + skippedAnnouncementCount)}
      </p>
      <p class="subtle">fehlgeschlagen oder übersprungen</p>
    </article>
  </section>

  {#if !readOnly}
    <div class="step-actions">
      <CTA
        type="primaryButton"
        content={finishing ? "Aktualisiere..." : "Fertig"}
        disabled={finishing}
        onClick={finish}
      />
    </div>
  {/if}
</div>

<style lang="less">
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .overview-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem;
  }

  .result-card {
    border: 1px solid color-mix(in srgb, var(--text-100) 22%, transparent);
    padding: 0.9rem;
    background: color-mix(in srgb, var(--text-100) 4%, transparent);
  }

  .result-card h3 {
    margin: 0 0 0.7rem;
    color: var(--green-100);
    font-size: 1rem;
  }

  .metric {
    margin: 0;
    color: var(--text-100);
    font-size: 1.35rem;
    font-weight: 800;
  }

  .subtle {
    margin: 0.35rem 0 0;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
    line-height: 1.35;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.4rem;
  }

  @media (max-width: 760px) {
    .overview-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
